import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AdditionalHeaders, CboRole, FilterReportType, Req, Res } from '@cbo/shared-library';
import { PosProvider } from '@cbo/shared-library/types/posProvider';
import useCallBsl from '../../utils/hooks/useCallBsl';
import {
  addNewLink,
  deleteCompanyLink as deleteCompanyLinkServiceFunction,
  editLink,
  getAllLinks,
} from '../../services/LinkService';
import toQueryParams from '../../utils/objectUtils/toQueryParams';
import { proxies } from '../requests';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { MessageResponse } from '../models/MessageResponse';
import { BaseDatasetColumns } from '../CustomReports/types';
import { FormulaToken } from '../../customMetrics/FormulaBuilder/FormulaBuilder';
import { ExportScheduleReportFileFormat } from '../../../sales/enums';
import { ScheduledReport, ScheduleStatus } from '../../scheduleReports/types';
import { ExportFileReportGridResponse } from '../../labor/types';
import { downloadFile } from '../../labor/utilities/laborUtilities';

const Verb = Req.Firebase.HttpVerb;

/**
 * Valid CBO roles that can view custom reports
 */
export const VALID_CUSTOM_REPORTS_VIEWER_ROLES = [
  CboRole.ABOVE_STORE_MANAGER_LINK_VIEWER,
  CboRole.ASSISTANT_MANAGER_LINK_VIEWER,
  CboRole.EMPLOYEE_LINK_VIEWER,
  CboRole.SHIFT_LEADER_LINK_VIEWER,
  CboRole.ADMIN_LINK_VIEWER,
  CboRole.SUPPORT_LINK_VIEWER,
  CboRole.STORE_MANAGER_LINK_VIEWER,
  CboRole.GENERAL_MANAGER_LINK_VIEWER,
  CboRole.ACCOUNTANT_LINK_VIEWER,
] as const;

/**
 * Type representing valid custom reports viewer roles
 */
export type CustomReportsViewerRole = (typeof VALID_CUSTOM_REPORTS_VIEWER_ROLES)[number];

export interface GetAllGlMappingsRequest {
  dataTypes?: string[];
  entityNames?: string[];
  glAccountNames?: string[];
}

export interface GlMappingsResponse {
  id?: number;
  dataType: string;
  entityName: string;
  glAccountName?: string;
  entityId: string;
  glAccountId?: string;
}

export interface GlAccountId {
  glAccountId: string;
}

export interface CreateUpdateGlMappingRequest extends GlAccountId {
  jobCodeDefinitionId: string;
}

export interface JobCodeMappingId {
  id: number;
}

export interface GlMappingFilterResponse {
  dataTypes: string[];
  entityNames: string[];
  glAccountNames: string[];
}

export interface OrganizationUser {
  username: string;
  email: string;
  familyName: string;
  fullName: string;
  givenName: string;
  primary: boolean;
  status: string;
  address?: {
    city?: string;
    state?: string;
  };
  accessType: string;
  displayUsername: string;
  idmUserIdentifier: string;
  sourceIdp: string;
  lastModifiedTimestamp: string;
  lastStatusChangeTimestamp?: string;
  internalSupportAccess?: string;
  createdTimestamp?: string;
}

export interface OrganizationUsersResponse {
  lastPage: boolean;
  pageNumber: number;
  totalPages: number;
  totalResults: number;
  pageContent: OrganizationUser[];
}

export interface PublishReportPayload {
  reportId: number;
  usernames: string[];
  userRoles: string[];
}

export interface TotalUnreadNotifications {
  totalUnread: number;
}

export interface NotificationDetails extends TotalUnreadNotifications {
  data: Res.Notifications.Notification[];
  totalPages: number;
  totalResults: number;
}
export interface NotificationResponse extends NotificationDetails {
  lastPage: boolean;
  pageNumber: number;
}

export interface CustomReportBase {
  id: number;
  title: string;
}
export interface CustomReportSummary extends CustomReportBase {
  description: string;
  creator?: string;
  url?: string;
  category?: string;
  permissions?: string[];
  isPublished?: boolean;
  lastPublished: Date | null;
}

export interface DeleteCustomReportResult {
  deletedAt: string;
  id: string;
}

export interface ColumnDefinition {
  id: number;
  columnName: string;
  columnType: string;
  isNumeric: boolean;
  filterType?: FilterReportType;
  customMetricId?: string;
  customMetricFormat?: string;
  isCustomMetric?: boolean;
}

export interface CustomReportsDatasetOutput {
  datasetName: string;
  columns: ColumnDefinition[];
}

export interface CustomReports {
  id: string;
  title: string;
  category: string;
  description: string;
  creator: string;
}

export interface GroupedColumn {
  field: string;
  sortDirection?: 'asc' | 'desc';
}

export interface ComparativeColumn {
  field: string;
  dateRange: string;
}

export interface DatagridElement {
  type: string;
  name?: string;
  columns: Record<string, string[]>;
  groupedColumns?: Record<string, GroupedColumn[]>;
  comparativeColumns?: Record<string, ComparativeColumn[]>;
  customColumns?: Record<string, string>;
  showSummaryRow?: boolean;
  defaultSort?: Array<{ field: string; sortDirection: 'asc' | 'desc' }>;
}

export interface DatasetConfig {
  selectedColumns: string[];
  customMetricIds?: string[];
}

export interface DetailsAndConfig {
  data: Record<string, DatasetConfig>;
  additionalFilters: FilterReportType[];
  elements: DatagridElement[][];
}

export interface CustomReportData {
  title: string;
  description?: string;
  category: string;
  detailsAndConfig: DetailsAndConfig;
  requiredViewRoles?: CboRole[];
  requiredEditRoles?: CboRole[];
  updatedBy?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface CustomReportMetadata extends CustomReportBase {
  organizationId: string;
  description: string;
  category: string;
  detailsAndConfig: DetailsAndConfig;
  requiredViewRole?: CboRole[];
  requiredEditRoles?: CboRole[];
  createdBy: string;
  updatedBy: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  isPublished: boolean;
}

export interface CustomMetricData {
  name?: string;
  formulaTokens?: FormulaToken[];
  dataset?: string;
  description?: string;
  metricType?: string;
  format?: string;
}

export interface PublishReportData {
  reportId: number;
  userIds?: string[];
  userRoles?: CustomReportsViewerRole[];
}

export interface UpdatePublishReportData {
  reportId: number;
  addUserIds?: string[];
  removeUserIds?: string[];
  addUserRoles?: CustomReportsViewerRole[];
  removeUserRoles?: CustomReportsViewerRole[];
}

export enum ReportShareType {
  DIRECT = 'DIRECT',
  ROLE_BASED = 'ROLE_BASED',
}
export interface CustomReportShares {
  id: number;
  shareType: ReportShareType;
  sharedWithRoles?: CustomReportsViewerRole[];
  sharedWithUserId?: string;
  createdBy: string;
  createdAt: Date;
}

export interface CustomReportId {
  id: number;
}

export interface FilterValueDto {
  filterType: FilterReportType;
  values: string[];
}

export interface ComparativeColumnRequest {
  field: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

export interface ReportFilterWithValuesInput {
  startDate: string;
  endDate: string;
  siteIds: string[];
  filterValues?: FilterValueDto[];
  comparativeColumns?: ComparativeColumnRequest[];
}

export interface ExecuteCustomReportRequest {
  id?: number;
  detailsAndConfig?: DetailsAndConfig;
  queryRequest: ReportFilterWithValuesInput;
}

export interface ExecuteCustomReportResponse {
  id?: number;
  results: Partial<Record<BaseDatasetColumns, string | number | boolean | null>>[];
}

export interface CustomMetric {
  id: string;
  name: string;
  formula: string;
  formulaTokens: FormulaToken[];
  dataset: string;
  description: string;
  metricType: string;
  format: string;
  usageCount: number;
  createdBy: string;
  updatedAt: string;
}

export interface BaseCustomMetricResponse {
  success: boolean;
  message: string;
}
export interface CustomMetricSaveResponse extends BaseCustomMetricResponse {
  id: string;
}

export interface CustomMetricsResponse {
  lastPage: boolean;
  pageNumber: number;
  totalPages: number;
  totalResults: number;
  pageContent: CustomMetric[];
}

export interface IFilter {
  field: string;
  header: string;
}

export interface IReportFilters {
  filters: IFilter[];
}

export interface IReports extends IReportFilters {
  headers: IReportFilters;
}
export interface IReportsHeader {
  [key: string]: IReports;
}

export interface ReportTypesListResponse {
  reportHeaders: IReportsHeader;
  reportTypes: string[];
}

export interface ReportListResponse {
  title: string;
  id: string;
  type: string;
}

export interface ReportDetails extends CustomReportData {
  id: number;
  type: string;
  creator: string;
  isPublished: boolean;
  lastPublished: string | null;
}
export interface ObjectType {
  [key: string]: string;
}
export interface ReportDefinitionType extends ObjectType {
  reportType: string;
  reportId: string;
  reportName: string;
}
export interface ScheduleReportsResponse {
  report: Record<string, unknown>;
  exportFileFormat: ExportScheduleReportFileFormat;
  recipientSecurity: boolean;
  scheduleName: string;
  tenantId: string;
  ownerId: string;
  reportName: string;
  description: string;
  recipients: ObjectType[];
  reportDefinition: ReportDefinitionType;
  recurrenceType?: string;
  startDate: string;
  endDate: string;
  runTime: string;
  timezone: string;
  deliveryFormat: string;
  isSecurityEnabled: boolean;
  reportSettings: ObjectType;
  daysOfWeek: string[];
}

export interface UpdateScheduleReportsResponse extends ScheduleReportsResponse {
  scheduleId: string;
}

export interface GetScheduledReportsResponse {
  scheduledByYou: number | undefined;
  scheduledToYou: number | undefined;
  schedules: ScheduledReport[];
  total: number | undefined;
}

export interface DeleteScheduleResponse {
  id: string;
  name: string;
  status: ScheduleStatus;
  message: string;
}

export interface CustomReportExportResponse {
  results: ExportFileReportGridResponse;
}

export type AdminRequests = {
  getUserDashboardConfigurationData: () => Promise<Res.Admin.DashboardConfigurationResponse>;
  updateUserPreferences: (prefs: Req.Admin.UserPreferences) => Promise<Res.Admin.UserPreferences>;
  getUserPreferences: () => Promise<Res.Admin.UserPreferences>;
  getAllCompanyLinks: (filterByUserRoles?: boolean) => Promise<Res.Firebase.Link[]>;
  addNewCompanyLink: (link: Req.Firebase.LinkData) => Promise<Res.Firebase.Link>;
  editCompanyLink: (link: Req.Firebase.Link) => Promise<Res.Firebase.Link>;
  deleteCompanyLink: (linkId: string) => Promise<string>;
  getAllNotificationSettingsData: () => Promise<Res.Notifications.NotificationSettings[]>;
  getNotificationSettingsData: (id: string) => Promise<Res.Notifications.NotificationSettings>;
  getAllEventTypesData: () => Promise<{ data: Res.Notifications.EventType[] | undefined }>;
  deleteNotificationSettingsData: (notificationSettingId: string) => Promise<{ rowsAffected: number }>;
  createNotificationSettingsData: (
    notificationSetting: Req.Notifications.NotificationSettingsData
  ) => Promise<Res.Notifications.NotificationSettings>;
  updateNotificationSettingsData: (
    id: string,
    notificationSetting: Req.Notifications.NotificationSettingsData
  ) => Promise<Res.Notifications.NotificationSettings>;
  getFiscalCalendarData: () => Promise<Res.Calendar.FiscalCalendar>;
  createFiscalCalendar: (fiscalCalendar: Req.Calendar.FiscalCalendarData) => Promise<Res.Calendar.FiscalCalendar>;
  updateFiscalCalendar: (
    id: string,
    fiscalCalendar: Req.Calendar.FiscalCalendarData
  ) => Promise<Res.Calendar.FiscalCalendar>;
  getPayrollCalendarData: (siteId: string) => Promise<Res.Calendar.PayrollCalendar | MessageResponse>;
  upsertPayrollCalendar: (payrollCalendar: Req.Calendar.PayrollCalendarData) => Promise<Res.Calendar.PayrollCalendar>;
  getAllSitesData: (siteIds?: string[]) => Promise<Res.SiteManagement.PagedResponse<Res.SiteManagement.SiteViewData>>;
  getSiteDetailsDataById: (id: string) => Promise<Res.SiteManagement.SiteViewData>;
  getSiteDetailsDataByIds: (ids: string[]) => Promise<Res.SiteManagement.SiteViewData[]>;
  updateSiteDayParts: (
    siteDayParts: Req.SiteManagement.DayPart[],
    enterpriseUnitId: string
  ) => Promise<Res.SiteManagement.SiteViewData>;
  getAllAuditLogData: (payload: Req.AuditLog.FilterRequest) => Promise<Res.AuditLog.LogEntry>;
  getAllAuditLogFilters: () => Promise<Res.AuditLog.FilterResponse>;
  getNotificationsData: (pageNumber: number, pageSize: number) => Promise<NotificationResponse>;
  getTotalUnreadNotifications: () => Promise<TotalUnreadNotifications>;
  markAllNotificationAsRead: () => Promise<Res.Notifications.Notification>;
  getAllJobCodeMappings: (body: GetAllGlMappingsRequest) => Promise<GlMappingsResponse[] | undefined>;
  updateGlMapping: (
    id: string,
    body: CreateUpdateGlMappingRequest,
    jobCodeName: string
  ) => Promise<JobCodeMappingId | undefined>;
  createGlMapping: (body: CreateUpdateGlMappingRequest, jobCodeName: string) => Promise<JobCodeMappingId | undefined>;
  getGlMappingFilters: () => Promise<GlMappingFilterResponse | undefined>;
  deleteGlMapping: (id: string, jobCodeName: string) => Promise<JobCodeMappingId | undefined>;
  getAllGlMappings: (body: GetAllGlMappingsRequest) => Promise<GlMappingsResponse | void>;
  getPosTypeForOrg: () => Promise<{
    posProvider: PosProvider | null;
    addOns: string[];
  }>;
  getCustomReports: () => Promise<CustomReportSummary[]>;
  updateCustomReport: (id: number, reportData: Partial<CustomReportData>) => Promise<CustomReportMetadata>;
  getCustomReport: (id: number | null, siteId: string) => Promise<CustomReportData>;
  getSharedCustomReports: (userId: string, userRoles: CustomReportsViewerRole[]) => Promise<CustomReportSummary[]>;
  getCustomReportsDatasets: () => Promise<CustomReportsDatasetOutput[]>;
  saveCustomReport: (customReport: CustomReportData) => Promise<CustomReportId>;
  executeCustomReport: (request: ExecuteCustomReportRequest) => Promise<ExecuteCustomReportResponse>;
  exportCustomReport: (request: ExecuteCustomReportRequest) => Promise<CustomReportExportResponse | void>;
  publishCustomReport: (publishReportData: PublishReportData) => Promise<string>;
  unpublishCustomReport: (unpublishReportData: Pick<PublishReportData, 'reportId'>) => Promise<string>;
  updatePublishCustomReport: (updatePublishReportData: UpdatePublishReportData) => Promise<string>;
  getCustomReportShares: (reportId: number) => Promise<CustomReportShares[]>;
  getAllCustomMetrics: (page: number, limit: number) => Promise<CustomMetricsResponse>;
  getCustomMetric: (id: string) => Promise<CustomMetric>;
  getOrganizationUsers: () => Promise<OrganizationUser[]>;
  saveCustomMetric: (customMetric: CustomMetricData) => Promise<CustomMetricSaveResponse>;
  getReportsByMetricId: (metricId: string) => Promise<CustomReportBase[]>;
  updateCustomMetric: (metricId: string, metric: CustomMetricData) => Promise<CustomMetricSaveResponse>;
  deleteCustomReport: (reportId: number) => Promise<DeleteCustomReportResult>;
  deleteCustomMetric: (id: string) => Promise<BaseCustomMetricResponse>;
  saveScheduleReport: (
    scheduleReport: ScheduleReportsResponse,
    reoccurenceType: string
  ) => Promise<ScheduleReportsResponse>;
  updateScheduleReport: (
    scheduleReport: UpdateScheduleReportsResponse,
    scheduleId: string
  ) => Promise<UpdateScheduleReportsResponse>;
  getPredefinedReportsList: () => Promise<ReportTypesListResponse>;
  getAllReportsList: () => Promise<ReportListResponse[]>;
  getReportsById: (id: string) => Promise<ReportDetails>;
  getScheduledReports: () => Promise<GetScheduledReportsResponse>;
  getScheduledReportsByReportId: (reportId: number) => Promise<GetScheduledReportsResponse>;
  getScheduledReportById: (id: string) => Promise<ScheduledReport>;
  deleteSchedule: (id: string) => Promise<DeleteScheduleResponse>;
};

export const useAdminRequests = (): AdminRequests => {
  const callBsl = useCallBsl();
  const { setSnackbarState } = useSnackbar();
  const { t } = useTranslation();

  const getUserDashboardConfigurationData = async (): Promise<Res.Admin.DashboardConfigurationResponse> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.GET,
      pathSegments: ['dashboard-config'],
    });

  const updateUserPreferences = async (prefs: Req.Admin.UserPreferences): Promise<Res.Admin.UserPreferences> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.POST,
      pathSegments: ['users', 'preferences'],
      payload: prefs,
    });

  const getUserPreferences = async (): Promise<Res.Admin.UserPreferences> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.GET,
      pathSegments: ['users', 'preferences'],
    });

  const getPosTypeForOrg = async (): Promise<{
    posProvider: PosProvider | null;
    addOns: string[];
  }> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.GET,
      pathSegments: ['pos'],
    });

  const getCustomReports = async (): Promise<CustomReportSummary[]> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report'],
    });

  const getCustomReport = async (id: number | null, siteId: string): Promise<CustomReportData> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-report', 'findOne', id?.toString() ?? ''],
      payload: {
        queryRequest: {
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString(),
          siteIds: [siteId],
        },
      },
    });

  const updateCustomReport = async (id: number, reportData: Partial<CustomReportData>): Promise<CustomReportMetadata> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.PATCH,
      pathSegments: ['custom-report', id?.toString(), 'metadata'],
      payload: {
        ...reportData,
      },
    });

  const getSharedCustomReports = async (
    userId: string,
    userRoles: CustomReportsViewerRole[]
  ): Promise<CustomReportSummary[]> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report', 'shared', userId],
      queryParams: toQueryParams({ userRoles: userRoles.join(',') }),
    });

  /**
   * COMPANY LINKS
   */
  const getAllCompanyLinks = async (filterByUserRoles?: boolean): Promise<Res.Firebase.Link[]> =>
    getAllLinks(filterByUserRoles);

  const addNewCompanyLink = async (link: Req.Firebase.LinkData): Promise<Res.Firebase.Link> => addNewLink(link);

  const editCompanyLink = async (link: Req.Firebase.Link): Promise<Res.Firebase.Link> => editLink(link);

  const deleteCompanyLink = async (linkId: string): Promise<string> => deleteCompanyLinkServiceFunction(linkId);

  /**
   * NOTIFICATIONS
   */
  const getAllNotificationSettingsData = async (): Promise<Res.Notifications.NotificationSettings[]> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.GET,
      pathSegments: ['settings'],
    });

  const getNotificationsData = async (pageNumber: number, pageSize: number): Promise<NotificationResponse> =>
    callBsl<NotificationResponse>({
      proxy: proxies.notifications,
      verb: Req.Firebase.HttpVerb.GET,
      pathSegments: ['notifications'],
      queryParams: toQueryParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
      }),
    });

  const getTotalUnreadNotifications = async (): Promise<TotalUnreadNotifications> =>
    callBsl<TotalUnreadNotifications>({
      proxy: proxies.notifications,
      verb: Req.Firebase.HttpVerb.GET,
      pathSegments: ['notifications', 'total-unread'],
    });

  const markAllNotificationAsRead = async (): Promise<Res.Notifications.Notification> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.PUT,
      pathSegments: ['notifications', 'mark-all-as-read'],
    });

  const getNotificationSettingsData = async (id: string): Promise<Res.Notifications.NotificationSettings> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.GET,
      pathSegments: ['settings', id],
      queryParams: toQueryParams({ includeEventTypes: 'true' }),
    });

  const getAllEventTypesData = async (): Promise<{ data: Res.Notifications.EventType[] }> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.GET,
      pathSegments: ['event-types'],
    });

  const createNotificationSettingsData = async (
    settings: Req.Notifications.NotificationSettingsData
  ): Promise<Res.Notifications.NotificationSettings> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.POST,
      pathSegments: ['settings'],
      payload: settings,
    });

  const updateNotificationSettingsData = async (
    id: string,
    settings: Req.Notifications.NotificationSettingsData
  ): Promise<Res.Notifications.NotificationSettings> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.PUT,
      pathSegments: ['settings', id],
      payload: settings,
    });

  const deleteNotificationSettingsData = async (id: string): Promise<{ rowsAffected: number }> =>
    callBsl({
      proxy: proxies.notifications,
      verb: Verb.DELETE,
      pathSegments: ['settings', id],
    });

  /**
   * FISCAL CALENDAR
   */
  const getFiscalCalendarData = async (): Promise<Res.Calendar.FiscalCalendar> =>
    callBsl({
      proxy: proxies.calendar,
      verb: Verb.GET,
      pathSegments: ['fiscal-calendar'],
    });

  const createFiscalCalendar = async (
    fiscalCalendar: Req.Calendar.FiscalCalendarData
  ): Promise<Res.Calendar.FiscalCalendar> =>
    callBsl({
      proxy: proxies.calendar,
      verb: Verb.POST,
      pathSegments: ['fiscal-calendar'],
      payload: fiscalCalendar,
    });

  const updateFiscalCalendar = async (
    id: string,
    fiscalCalendar: Req.Calendar.FiscalCalendarData
  ): Promise<Res.Calendar.FiscalCalendar> =>
    callBsl({
      proxy: proxies.calendar,
      verb: Verb.PUT,
      pathSegments: ['fiscal-calendar', id],
      payload: fiscalCalendar,
    });

  const getPayrollCalendarData = async (siteId: string): Promise<Res.Calendar.PayrollCalendar | MessageResponse> =>
    callBsl({
      proxy: proxies.calendar,
      verb: Verb.GET,
      pathSegments: ['payroll-calendars', 'find'],
      queryParams: toQueryParams({ siteId }),
    });

  const upsertPayrollCalendar = async (
    payrollCalendar: Req.Calendar.PayrollCalendarData
  ): Promise<Res.Calendar.PayrollCalendar> =>
    callBsl({
      proxy: proxies.calendar,
      verb: Verb.POST,
      pathSegments: ['payroll-calendars'],
      payload: payrollCalendar,
      enterpriseUnit: payrollCalendar.siteIds[0],
    });

  /**
   * Site Management
   */
  const getAllSitesData = async (
    siteIds?: string[]
  ): Promise<Res.SiteManagement.PagedResponse<Res.SiteManagement.SiteViewData>> => {
    let additionalHeaders;
    if (siteIds) {
      additionalHeaders = siteIds.map((siteId) => ({
        key: AdditionalHeaders.SITE_ID,
        value: siteId,
      }));
    }
    return callBsl({
      proxy: proxies.siteManagement,
      verb: Verb.GET,
      pathSegments: ['sites'],
      additionalHeaders,
    });
  };

  const getSiteDetailsDataById = async (siteId: string): Promise<Res.SiteManagement.SiteViewData> =>
    callBsl({
      proxy: proxies.siteManagement,
      verb: Verb.GET,
      pathSegments: ['sites', siteId],
    });

  const getSiteDetailsDataByIds = async (siteIds: string[]): Promise<Res.SiteManagement.SiteViewData[]> =>
    callBsl({
      proxy: proxies.siteManagement,
      verb: Verb.POST,
      pathSegments: ['sites', 'find-by-ids'],
      payload: siteIds,
    });

  const updateSiteDayParts = async (
    siteDayParts: Req.SiteManagement.DayPart[],
    enterpriseUnitId: string
  ): Promise<Res.SiteManagement.SiteViewData> =>
    callBsl({
      proxy: proxies.siteManagement,
      verb: Verb.PATCH,
      enterpriseUnit: enterpriseUnitId,
      pathSegments: ['sites', 'day-parts'],
      payload: { dayparts: siteDayParts },
    });

  /**
   * Audit Log
   */
  const getAllAuditLogData = async (payload: Req.AuditLog.FilterRequest): Promise<Res.AuditLog.LogEntry> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.POST,
      pathSegments: ['audit-log'],
      payload,
    });

  const getAllAuditLogFilters = async (): Promise<Res.AuditLog.FilterResponse> =>
    callBsl({
      proxy: proxies.admin,
      verb: Verb.GET,
      pathSegments: ['audit-log', 'filters'],
    });

  /**
   * GL mapping
   */

  // helper method to get query params for GL mappings
  const getGlMappingsParams = (
    body: GetAllGlMappingsRequest
  ): Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> => {
    const queryParams: Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> = [
      { kind: 'multi', key: 'dataTypes', value: body.dataTypes && body.dataTypes.length > 0 ? body.dataTypes : [] },
      {
        kind: 'multi',
        key: 'entityNames',
        value: body.entityNames && body.entityNames.length > 0 ? body.entityNames : [],
      },
      {
        kind: 'multi',
        key: 'glAccountNames',
        value: body.glAccountNames && body.glAccountNames.length > 0 ? body.glAccountNames : [],
      },
    ];
    return queryParams;
  };

  const getAllJobCodeMappings = useCallback(
    async (body: GetAllGlMappingsRequest) =>
      callBsl<GlMappingsResponse[]>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['job-code-definition-mapping'],
        queryParams: body ? getGlMappingsParams(body) : [],
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const getGlMappingFilters = useCallback(
    async () =>
      callBsl<GlMappingFilterResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['job-code-definition-mapping', 'filters'],
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const updateGlMapping = useCallback(
    async (id: string, body: CreateUpdateGlMappingRequest, jobCodeName: string) =>
      callBsl<JobCodeMappingId>({
        proxy: proxies.glAccounts,
        verb: Verb.PUT,
        pathSegments: ['job-code-definition-mapping', id],
        payload: body,
        queryParams: toQueryParams({
          jobCodeName,
        }),
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const createGlMapping = useCallback(
    async (body: CreateUpdateGlMappingRequest, jobCodeName: string) =>
      callBsl<JobCodeMappingId>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['job-code-definition-mapping'],
        payload: body,
        queryParams: toQueryParams({
          jobCodeName,
        }),
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const deleteGlMapping = useCallback(
    async (id: string, jobCodeName: string) =>
      callBsl<JobCodeMappingId>({
        proxy: proxies.glAccounts,
        verb: Verb.DELETE,
        pathSegments: ['job-code-definition-mapping', id],
        queryParams: toQueryParams({
          jobCodeName,
        }),
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const getAllGlMappings = useCallback(
    async (body: GetAllGlMappingsRequest) =>
      callBsl<GlMappingsResponse>({
        verb: Verb.GET,
        proxy: proxies.glAccounts,
        pathSegments: ['gl-mapping'],
        queryParams: body ? getGlMappingsParams(body) : [],
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  /**
   * Custom Reports
   */
  const getCustomReportsDatasets = async (): Promise<CustomReportsDatasetOutput[]> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report', 'datasets'],
    });

  const saveCustomReport = async (customReport: CustomReportData): Promise<CustomReportId> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-report'],
      payload: customReport,
    });

  const executeCustomReport = async (request: ExecuteCustomReportRequest): Promise<ExecuteCustomReportResponse> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-report', 'execute'],
      payload: request,
    });

  const exportCustomReport = async (
    request: ExecuteCustomReportRequest
  ): Promise<CustomReportExportResponse | void> => {
    const data: CustomReportExportResponse = await callBsl({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-report', 'execute', 'export'],
      payload: request,
    });
    if (data.results.signedUrl) {
      downloadFile(data.results.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const deleteCustomReport = async (reportId: number): Promise<DeleteCustomReportResult> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.DELETE,
      pathSegments: ['custom-report', reportId.toString()],
    });

  const publishCustomReport = async (publishReportData: PublishReportData): Promise<string> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-report', 'publish'],
      payload: publishReportData,
    });

  const unpublishCustomReport = async (unpublishReportData: Pick<PublishReportData, 'reportId'>): Promise<string> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.DELETE,
      pathSegments: ['custom-report', 'publish'],
      payload: unpublishReportData,
    });

  const updatePublishCustomReport = async (updatePublishReportData: UpdatePublishReportData): Promise<string> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.PATCH,
      pathSegments: ['custom-report', 'publish'],
      payload: updatePublishReportData,
    });

  const getCustomReportShares = async (reportId: number): Promise<CustomReportShares[]> =>
    callBsl({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report', reportId.toString(), 'shares'],
    });
  /**
   * Custom Metrics
   */
  const getAllCustomMetrics = async (page: number, limit: number): Promise<CustomMetricsResponse> => {
    const queryParams: Array<Req.Firebase.SingleQueryParam> = [
      { kind: 'single', key: 'pagenum', value: page.toString() },
      { kind: 'single', key: 'pagesize', value: limit.toString() },
    ];

    return callBsl<CustomMetricsResponse>({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-metrics'],
      queryParams,
    });
  };

  const getCustomMetric = async (id: string): Promise<CustomMetric> =>
    callBsl<CustomMetric>({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-metrics', id],
    });

  const getReportsByMetricId = async (metricId: string): Promise<CustomReportBase[]> =>
    callBsl<CustomReportBase[]>({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-metrics', metricId, 'reports'],
    });

  const updateCustomMetric = async (id: string, metric: CustomMetricData): Promise<CustomMetricSaveResponse> =>
    callBsl<CustomMetricSaveResponse>({
      proxy: proxies.reporting,
      verb: Verb.PATCH,
      pathSegments: ['custom-metrics', id],
      payload: metric,
    });

  const deleteCustomMetric = async (id: string): Promise<BaseCustomMetricResponse> =>
    callBsl<BaseCustomMetricResponse>({
      proxy: proxies.reporting,
      verb: Verb.DELETE,
      pathSegments: ['custom-metrics', id],
    });

  /**
   * Organization Users for Report Publishing
   */
  const getOrganizationUsers = async (): Promise<OrganizationUser[]> => {
    const response = await callBsl<OrganizationUsersResponse>({
      proxy: proxies.admin,
      verb: Verb.GET,
      pathSegments: ['recipient-list', 'organization-users'],
    });
    return response.pageContent || [];
  };

  const saveCustomMetric = async (metric: CustomMetricData): Promise<CustomMetricSaveResponse> =>
    callBsl<CustomMetricSaveResponse>({
      proxy: proxies.reporting,
      verb: Verb.POST,
      pathSegments: ['custom-metrics'],
      payload: metric,
    });

  const getPredefinedReportsList = async (): Promise<ReportTypesListResponse> =>
    callBsl<ReportTypesListResponse>({
      proxy: proxies.taskScheduling,
      verb: Verb.GET,
      pathSegments: ['schedules', 'predefined'],
    });

  const getAllReportsList = async (): Promise<ReportListResponse[]> =>
    callBsl<ReportListResponse[]>({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report', 'list'],
    });

  const getReportsById = async (id: string): Promise<ReportDetails> =>
    callBsl<ReportDetails>({
      proxy: proxies.reporting,
      verb: Verb.GET,
      pathSegments: ['custom-report', id, 'details'],
    });

  const saveScheduleReport = async (
    scheduleReport: ScheduleReportsResponse,
    reoccurrenceType: string
  ): Promise<ScheduleReportsResponse> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.POST,
      pathSegments: ['schedules', reoccurrenceType],
      payload: scheduleReport,
    });

  const updateScheduleReport = async (
    scheduleReport: UpdateScheduleReportsResponse,
    scheduleId: string
  ): Promise<UpdateScheduleReportsResponse> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.PUT,
      pathSegments: ['schedules', scheduleId],
      payload: scheduleReport,
    });

  const getScheduledReports = async (): Promise<GetScheduledReportsResponse> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.GET,
      pathSegments: ['schedules'],
    });

  const getScheduledReportsByReportId = async (reportId: number): Promise<GetScheduledReportsResponse> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.GET,
      pathSegments: ['schedules'],
      queryParams: toQueryParams({ reportId: String(reportId) }),
    });

  const getScheduledReportById = async (id: string): Promise<ScheduledReport> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.GET,
      pathSegments: ['schedules', id],
    });

  const deleteSchedule = async (id: string): Promise<DeleteScheduleResponse> =>
    callBsl({
      proxy: proxies.taskScheduling,
      verb: Verb.DELETE,
      pathSegments: ['schedules', id],
    });

  return {
    getUserDashboardConfigurationData,
    updateUserPreferences,
    getUserPreferences,
    getAllCompanyLinks,
    addNewCompanyLink,
    editCompanyLink,
    deleteCompanyLink,
    getAllNotificationSettingsData,
    getNotificationSettingsData,
    getAllEventTypesData,
    deleteNotificationSettingsData,
    createNotificationSettingsData,
    updateNotificationSettingsData,
    getFiscalCalendarData,
    createFiscalCalendar,
    updateFiscalCalendar,
    getPayrollCalendarData,
    upsertPayrollCalendar,
    getAllSitesData,
    getSiteDetailsDataById,
    getSiteDetailsDataByIds,
    updateSiteDayParts,
    getAllAuditLogData,
    getAllAuditLogFilters,
    getNotificationsData,
    getTotalUnreadNotifications,
    markAllNotificationAsRead,
    getAllJobCodeMappings,
    updateGlMapping,
    createGlMapping,
    getGlMappingFilters,
    deleteGlMapping,
    getAllGlMappings,
    getPosTypeForOrg,
    getCustomReport,
    updateCustomReport,
    getCustomReports,
    getSharedCustomReports,
    getCustomReportsDatasets,
    saveCustomReport,
    executeCustomReport,
    exportCustomReport,
    publishCustomReport,
    unpublishCustomReport,
    updatePublishCustomReport,
    getCustomReportShares,
    getAllCustomMetrics,
    getCustomMetric,
    getOrganizationUsers,
    saveCustomMetric,
    getReportsByMetricId,
    updateCustomMetric,
    deleteCustomReport,
    deleteCustomMetric,
    getPredefinedReportsList,
    saveScheduleReport,
    deleteSchedule,
    updateScheduleReport,
    getAllReportsList,
    getReportsById,
    getScheduledReports,
    getScheduledReportsByReportId,
    getScheduledReportById,
  };
};
